import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Ship, 
  Anchor, 
  Compass, 
  Waves,
  ArrowRight,
  Zap
} from 'lucide-react'

export default function DemoLoginPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const handleDemoLogin = () => {
    setIsLoading(true)
    // 模拟登录过程
    setTimeout(() => {
      navigate('/demo')
    }, 1000)
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* 背景动画 */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* 波浪动画 */}
      <div className="absolute bottom-0 left-0 right-0 h-32">
        <div className="absolute bottom-0 left-0 right-0 h-full bg-gradient-to-t from-blue-600/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-blue-500/30 to-transparent animate-wave"></div>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-blue-400/40 to-transparent animate-wave" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* 浮动元素 */}
      <div className="absolute top-20 left-10 animate-float">
        <Ship className="w-12 h-12 text-blue-300/50" />
      </div>
      <div className="absolute top-40 right-20 animate-float" style={{ animationDelay: '1s' }}>
        <Anchor className="w-8 h-8 text-blue-200/40" />
      </div>
      <div className="absolute bottom-40 left-20 animate-float" style={{ animationDelay: '2s' }}>
        <Compass className="w-10 h-10 text-blue-300/60" />
      </div>

      {/* 主要内容 */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-2xl mb-6">
              <Waves className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">TideLab</h1>
            <p className="text-blue-200">交易策略管理平台</p>
          </div>

          {/* Demo卡片 */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Demo 演示</h2>
              <p className="text-blue-200">体验完整的交易策略管理功能</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center text-sm text-blue-200">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                完整的策略管理功能
              </div>
              <div className="flex items-center text-sm text-blue-200">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                手动测试交易记录
              </div>
              <div className="flex items-center text-sm text-blue-200">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                回测结果分析
              </div>
              <div className="flex items-center text-sm text-blue-200">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                实盘追踪监控
              </div>
              <div className="flex items-center text-sm text-blue-200">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                研究笔记管理
              </div>
            </div>

            <button
              onClick={handleDemoLogin}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  正在进入演示...
                </>
              ) : (
                <>
                  一键进入演示
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>

            <div className="mt-4 text-center">
              <p className="text-xs text-blue-300/70 mb-4">
                所有数据均为模拟数据，仅供功能演示
              </p>
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-blue-300 hover:text-blue-200 transition-colors underline"
              >
                返回主登录页面
              </button>
            </div>
          </div>

          {/* 功能预览 */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="text-center">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Ship className="w-4 h-4 text-blue-300" />
                </div>
                <p className="text-xs text-blue-200">策略管理</p>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="text-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Anchor className="w-4 h-4 text-green-300" />
                </div>
                <p className="text-xs text-blue-200">交易记录</p>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="text-center">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Compass className="w-4 h-4 text-purple-300" />
                </div>
                <p className="text-xs text-blue-200">回测分析</p>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="text-center">
                <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Waves className="w-4 h-4 text-orange-300" />
                </div>
                <p className="text-xs text-blue-200">实盘追踪</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 